import { Badge } from './Badge';
import { Button } from './Button';

export interface TaskCardProps {
  title: string;
  description: string;
  stage: 'not-started' | 'awaiting-input' | 'working' | 'complete';
  overlay?: 'paused' | 'on-hold' | 'cancelled' | null;
  onHoldReason?: string;
  cancelledFromStage?: TaskCardProps['stage'];
  onResume?: () => void;
  onClick?: () => void;
  className?: string;
}

const STAGE_LABELS: Record<TaskCardProps['stage'], string> = {
  'not-started': 'Not Started',
  'awaiting-input': 'Awaiting Input',
  'working': 'Working',
  'complete': 'Complete',
};

const STAGE_BORDER: Record<TaskCardProps['stage'], string> = {
  'not-started': 'border-sd-text-muted',
  'awaiting-input': 'border-sd-warning',
  'working': 'border-sd-info',
  'complete': 'border-sd-success',
};

export function TaskCard({ title, description, stage, overlay, onHoldReason, cancelledFromStage, onResume, onClick, className = '' }: TaskCardProps) {
  const hasOverlay = overlay === 'paused' || overlay === 'on-hold' || overlay === 'cancelled';
  const isInteractive = !!onClick;

  return (
    <div
      {...(isInteractive && {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      })}
      className={`p-sd-3 rounded-sd-lg bg-sd-surface-elevated border-l-2 ${STAGE_BORDER[stage]} shadow-[0_1px_3px_rgba(0,0,0,0.06)] w-full transition-all duration-150 ease-in motion-reduce:transition-none ${isInteractive ? 'cursor-pointer hover:shadow-sd-sm hover:bg-sd-surface-elevated/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1' : ''} ${hasOverlay ? 'opacity-60 grayscale-[20%]' : ''} ${className}`.trim()}
    >
      <div className="flex flex-col gap-sd-1">
        <div className="flex items-center gap-sd-2">
          <Badge variant={stage}>{STAGE_LABELS[stage]}</Badge>
          {overlay === 'paused' && <Badge variant="warning">Paused</Badge>}
          {overlay === 'on-hold' && <Badge variant="warning">On Hold</Badge>}
          {overlay === 'cancelled' && <Badge variant="error">Cancelled</Badge>}
        </div>
        <span className={`text-sd-base font-medium text-sd-text ${overlay === 'cancelled' ? 'line-through' : ''}`}>{title}</span>
        <span className="text-sd-sm text-sd-text-secondary leading-relaxed">{description}</span>
        {overlay === 'on-hold' && onHoldReason && (
          <span className="text-sd-xs text-sd-text-muted">{onHoldReason}</span>
        )}
        {overlay === 'cancelled' && cancelledFromStage && (
          <span className="text-sd-xs text-sd-text-muted">Cancelled from {STAGE_LABELS[cancelledFromStage]}</span>
        )}
        {overlay === 'paused' && onResume && (
          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); onResume(); }}>Resume</Button>
        )}
      </div>
    </div>
  );
}
